// Client mirror of supabase/functions/_shared/badges-catalog.ts (round 2).
// Update both files in the same commit. The server is authoritative on
// whether a badge is *earned* (the array stored in users.badges); this
// client copy only drives rendering, ordering, progress display, and
// theme-set "X of Y categories" hints.

import { Trait } from '@web/features/collection/traits/type';

export { Trait };

export enum BadgeCode {
  // Holder count tiers
  HOLDER_I = 'HOLDER_I',
  HOLDER_X = 'HOLDER_X',
  HOLDER_L = 'HOLDER_L',

  // Collector (kept slim: backgrounds + friends)
  PALLET_TASTER_I = 'PALLET_TASTER_I',
  PALLET_TASTER_II = 'PALLET_TASTER_II',
  PALLET_TASTER_III = 'PALLET_TASTER_III',
  PET_PARENT_I = 'PET_PARENT_I',
  PET_PARENT_II = 'PET_PARENT_II',
  PET_PARENT_III = 'PET_PARENT_III',

  // Completionist
  BACK_3 = 'BACK_3',
  BACK_ALL = 'BACK_ALL',
  FRIENDS_FAMILY_ALL = 'FRIENDS_FAMILY_ALL',

  // Council
  COUNCIL_REG = 'COUNCIL_REG',
  COUNCIL_PRISM = 'COUNCIL_PRISM',
  COUNCIL_TIME = 'COUNCIL_TIME',

  // Rarity
  RARITY_TOP_1000 = 'RARITY_TOP_1000',
  RARITY_TOP_100 = 'RARITY_TOP_100',
  RARITY_TOP_10 = 'RARITY_TOP_10',

  // Themes
  PRISMATIC_I = 'PRISMATIC_I',
  PRISMATIC_II = 'PRISMATIC_II',
  PRISMATIC_III = 'PRISMATIC_III',
  TIME_MAGIC_I = 'TIME_MAGIC_I',
  TIME_MAGIC_II = 'TIME_MAGIC_II',
  TIME_MAGIC_III = 'TIME_MAGIC_III',
  PERSONA_THEME_I = 'PERSONA_THEME_I',
  PERSONA_THEME_II = 'PERSONA_THEME_II',
  PERSONA_THEME_III = 'PERSONA_THEME_III',
  RETRO_I = 'RETRO_I',
  RETRO_II = 'RETRO_II',
  RETRO_III = 'RETRO_III',
  ARCADE_I = 'ARCADE_I',
  ARCADE_III = 'ARCADE_III',
  MISATO_I = 'MISATO_I',
  MISATO_III = 'MISATO_III',
  YUSUKE_I = 'YUSUKE_I',
  YUSUKE_III = 'YUSUKE_III',
  NERV_FULL = 'NERV_FULL',
  HEART_I = 'HEART_I',
  HEART_III = 'HEART_III',
  MOON_I = 'MOON_I',
  MOON_III = 'MOON_III',

  // Meta
  BAD_HABIT = 'BAD_HABIT',
  POCKET_PET = 'POCKET_PET',
  GENESIS = 'GENESIS',

  // Collector rank (point-driven, server-injected)
  COLLECTOR_RANK_INITIATE = 'COLLECTOR_RANK_INITIATE',
  COLLECTOR_RANK_CONNOISSEUR = 'COLLECTOR_RANK_CONNOISSEUR',
  COLLECTOR_RANK_CURATOR = 'COLLECTOR_RANK_CURATOR',
  COLLECTOR_RANK_HALL_OF_FAME = 'COLLECTOR_RANK_HALL_OF_FAME',
}

export enum BadgeCategory {
  COLLECTOR_RANK = 'COLLECTOR_RANK',
  HOLDER = 'HOLDER',
  COLLECTOR = 'COLLECTOR',
  COMPLETIONIST = 'COMPLETIONIST',
  COUNCIL = 'COUNCIL',
  RARITY = 'RARITY',
  THEME = 'THEME',
  META = 'META',
}

export type BadgeTier = 'I' | 'II' | 'III' | 'IV';

export interface BadgeDefinition {
  code: BadgeCode;
  category: BadgeCategory;
  tier?: BadgeTier;
  tierGroupId?: string;
  threshold?: number;
  trait?: Trait;
  themeKeyword?: string;
  themeTraits?: Trait[];
  pointsThreshold?: number;
}

// Collector-rank tiers in ascending order. Mirror of
// supabase/functions/_shared/badges-catalog.ts COLLECTOR_RANK_TIERS.
export const COLLECTOR_RANK_TIERS: { code: BadgeCode; pointsThreshold: number }[] = [
  { code: BadgeCode.COLLECTOR_RANK_INITIATE,     pointsThreshold: 100 },
  { code: BadgeCode.COLLECTOR_RANK_CONNOISSEUR,  pointsThreshold: 500 },
  { code: BadgeCode.COLLECTOR_RANK_CURATOR,      pointsThreshold: 1500 },
  { code: BadgeCode.COLLECTOR_RANK_HALL_OF_FAME, pointsThreshold: 4000 },
];

interface ThemeDefinition { keyword: string; traits: Trait[]; }

export const THEME_DEFINITIONS = {
  PRISMATIC: {
    keyword: 'Prismatic',
    traits: [
      Trait.CLOTHING, Trait.EYES, Trait.HAIRCUTS, Trait.MOUTH,
      Trait.HEADWEARS, Trait.EYEWEAR, Trait.FRIENDS, Trait.EARRINGS,
    ],
  },
  TIME_MAGIC: {
    keyword: 'Time Magic',
    traits: [
      Trait.EYES, Trait.HAIRCUTS, Trait.TYPE, Trait.AURA,
      Trait.NECK, Trait.EARRINGS,
    ],
  },
  PERSONA_THEME: {
    keyword: 'Persona',
    traits: [
      Trait.BACKGROUND, Trait.CLOTHING, Trait.HEADWEARS,
      Trait.NECK, Trait.EARRINGS,
    ],
  },
  RETRO: {
    keyword: 'Retro',
    traits: [
      Trait.CLOTHING, Trait.HAIRCUTS, Trait.HEADWEARS,
      Trait.EYEWEAR, Trait.NECK,
    ],
  },
  ARCADE: { keyword: 'Arcade', traits: [Trait.CLOTHING, Trait.EYES, Trait.EYEWEAR] },
  MISATO: { keyword: 'Misato', traits: [Trait.CLOTHING, Trait.EYEWEAR, Trait.NECK] },
  YUSUKE: { keyword: 'Yusuke', traits: [Trait.CLOTHING, Trait.EYEWEAR, Trait.NECK] },
  NERV:   { keyword: 'NERV',   traits: [Trait.CLOTHING, Trait.HEADWEARS] },
  HEART:  { keyword: 'Heart',  traits: [Trait.FACE, Trait.EYEWEAR, Trait.NECK] },
  MOON:   { keyword: 'Moon',   traits: [Trait.HEADWEARS, Trait.NECK, Trait.EARRINGS] },
} satisfies Record<string, ThemeDefinition>;

export const BADGES: Record<BadgeCode, BadgeDefinition> = {
  [BadgeCode.HOLDER_I]: { code: BadgeCode.HOLDER_I, category: BadgeCategory.HOLDER, tier: 'I',   tierGroupId: 'HOLDER', threshold: 1 },
  [BadgeCode.HOLDER_X]: { code: BadgeCode.HOLDER_X, category: BadgeCategory.HOLDER, tier: 'II',  tierGroupId: 'HOLDER', threshold: 10 },
  [BadgeCode.HOLDER_L]: { code: BadgeCode.HOLDER_L, category: BadgeCategory.HOLDER, tier: 'III', tierGroupId: 'HOLDER', threshold: 50 },

  [BadgeCode.PALLET_TASTER_I]:   { code: BadgeCode.PALLET_TASTER_I,   category: BadgeCategory.COLLECTOR, tier: 'I',   tierGroupId: 'PALLET', threshold: 3,  trait: Trait.BACKGROUND },
  [BadgeCode.PALLET_TASTER_II]:  { code: BadgeCode.PALLET_TASTER_II,  category: BadgeCategory.COLLECTOR, tier: 'II',  tierGroupId: 'PALLET', threshold: 6,  trait: Trait.BACKGROUND },
  [BadgeCode.PALLET_TASTER_III]: { code: BadgeCode.PALLET_TASTER_III, category: BadgeCategory.COLLECTOR, tier: 'III', tierGroupId: 'PALLET', threshold: 10, trait: Trait.BACKGROUND },

  [BadgeCode.PET_PARENT_I]:   { code: BadgeCode.PET_PARENT_I,   category: BadgeCategory.COLLECTOR, tier: 'I',   tierGroupId: 'PET', threshold: 3,  trait: Trait.FRIENDS },
  [BadgeCode.PET_PARENT_II]:  { code: BadgeCode.PET_PARENT_II,  category: BadgeCategory.COLLECTOR, tier: 'II',  tierGroupId: 'PET', threshold: 8,  trait: Trait.FRIENDS },
  [BadgeCode.PET_PARENT_III]: { code: BadgeCode.PET_PARENT_III, category: BadgeCategory.COLLECTOR, tier: 'III', tierGroupId: 'PET', threshold: 15, trait: Trait.FRIENDS },

  [BadgeCode.BACK_3]:             { code: BadgeCode.BACK_3,             category: BadgeCategory.COMPLETIONIST, tier: 'I',  tierGroupId: 'BACK', threshold: 3, trait: Trait.BACK },
  [BadgeCode.BACK_ALL]:           { code: BadgeCode.BACK_ALL,           category: BadgeCategory.COMPLETIONIST, tier: 'II', tierGroupId: 'BACK', threshold: 6, trait: Trait.BACK },
  [BadgeCode.FRIENDS_FAMILY_ALL]: { code: BadgeCode.FRIENDS_FAMILY_ALL, category: BadgeCategory.COMPLETIONIST, trait: Trait.FRIENDS },

  [BadgeCode.COUNCIL_REG]:   { code: BadgeCode.COUNCIL_REG,   category: BadgeCategory.COUNCIL, trait: Trait.COUNCIL },
  [BadgeCode.COUNCIL_PRISM]: { code: BadgeCode.COUNCIL_PRISM, category: BadgeCategory.COUNCIL, trait: Trait.COUNCIL },
  [BadgeCode.COUNCIL_TIME]:  { code: BadgeCode.COUNCIL_TIME,  category: BadgeCategory.COUNCIL, trait: Trait.COUNCIL },

  [BadgeCode.RARITY_TOP_1000]: { code: BadgeCode.RARITY_TOP_1000, category: BadgeCategory.RARITY, tier: 'I',   tierGroupId: 'RARITY', threshold: 1000 },
  [BadgeCode.RARITY_TOP_100]:  { code: BadgeCode.RARITY_TOP_100,  category: BadgeCategory.RARITY, tier: 'II',  tierGroupId: 'RARITY', threshold: 100 },
  [BadgeCode.RARITY_TOP_10]:   { code: BadgeCode.RARITY_TOP_10,   category: BadgeCategory.RARITY, tier: 'III', tierGroupId: 'RARITY', threshold: 10 },

  [BadgeCode.PRISMATIC_I]:   { code: BadgeCode.PRISMATIC_I,   category: BadgeCategory.THEME, tier: 'I',   tierGroupId: 'PRISMATIC', threshold: 2, themeKeyword: 'Prismatic', themeTraits: THEME_DEFINITIONS.PRISMATIC.traits },
  [BadgeCode.PRISMATIC_II]:  { code: BadgeCode.PRISMATIC_II,  category: BadgeCategory.THEME, tier: 'II',  tierGroupId: 'PRISMATIC', threshold: 5, themeKeyword: 'Prismatic', themeTraits: THEME_DEFINITIONS.PRISMATIC.traits },
  [BadgeCode.PRISMATIC_III]: { code: BadgeCode.PRISMATIC_III, category: BadgeCategory.THEME, tier: 'III', tierGroupId: 'PRISMATIC', threshold: 8, themeKeyword: 'Prismatic', themeTraits: THEME_DEFINITIONS.PRISMATIC.traits },

  [BadgeCode.TIME_MAGIC_I]:   { code: BadgeCode.TIME_MAGIC_I,   category: BadgeCategory.THEME, tier: 'I',   tierGroupId: 'TIME_MAGIC', threshold: 2, themeKeyword: 'Time Magic', themeTraits: THEME_DEFINITIONS.TIME_MAGIC.traits },
  [BadgeCode.TIME_MAGIC_II]:  { code: BadgeCode.TIME_MAGIC_II,  category: BadgeCategory.THEME, tier: 'II',  tierGroupId: 'TIME_MAGIC', threshold: 4, themeKeyword: 'Time Magic', themeTraits: THEME_DEFINITIONS.TIME_MAGIC.traits },
  [BadgeCode.TIME_MAGIC_III]: { code: BadgeCode.TIME_MAGIC_III, category: BadgeCategory.THEME, tier: 'III', tierGroupId: 'TIME_MAGIC', threshold: 6, themeKeyword: 'Time Magic', themeTraits: THEME_DEFINITIONS.TIME_MAGIC.traits },

  [BadgeCode.PERSONA_THEME_I]:   { code: BadgeCode.PERSONA_THEME_I,   category: BadgeCategory.THEME, tier: 'I',   tierGroupId: 'PERSONA_THEME', threshold: 2, themeKeyword: 'Persona', themeTraits: THEME_DEFINITIONS.PERSONA_THEME.traits },
  [BadgeCode.PERSONA_THEME_II]:  { code: BadgeCode.PERSONA_THEME_II,  category: BadgeCategory.THEME, tier: 'II',  tierGroupId: 'PERSONA_THEME', threshold: 4, themeKeyword: 'Persona', themeTraits: THEME_DEFINITIONS.PERSONA_THEME.traits },
  [BadgeCode.PERSONA_THEME_III]: { code: BadgeCode.PERSONA_THEME_III, category: BadgeCategory.THEME, tier: 'III', tierGroupId: 'PERSONA_THEME', threshold: 5, themeKeyword: 'Persona', themeTraits: THEME_DEFINITIONS.PERSONA_THEME.traits },

  [BadgeCode.RETRO_I]:   { code: BadgeCode.RETRO_I,   category: BadgeCategory.THEME, tier: 'I',   tierGroupId: 'RETRO', threshold: 2, themeKeyword: 'Retro', themeTraits: THEME_DEFINITIONS.RETRO.traits },
  [BadgeCode.RETRO_II]:  { code: BadgeCode.RETRO_II,  category: BadgeCategory.THEME, tier: 'II',  tierGroupId: 'RETRO', threshold: 4, themeKeyword: 'Retro', themeTraits: THEME_DEFINITIONS.RETRO.traits },
  [BadgeCode.RETRO_III]: { code: BadgeCode.RETRO_III, category: BadgeCategory.THEME, tier: 'III', tierGroupId: 'RETRO', threshold: 5, themeKeyword: 'Retro', themeTraits: THEME_DEFINITIONS.RETRO.traits },

  [BadgeCode.ARCADE_I]:   { code: BadgeCode.ARCADE_I,   category: BadgeCategory.THEME, tier: 'I',   tierGroupId: 'ARCADE', threshold: 2, themeKeyword: 'Arcade', themeTraits: THEME_DEFINITIONS.ARCADE.traits },
  [BadgeCode.ARCADE_III]: { code: BadgeCode.ARCADE_III, category: BadgeCategory.THEME, tier: 'III', tierGroupId: 'ARCADE', threshold: 3, themeKeyword: 'Arcade', themeTraits: THEME_DEFINITIONS.ARCADE.traits },

  [BadgeCode.MISATO_I]:   { code: BadgeCode.MISATO_I,   category: BadgeCategory.THEME, tier: 'I',   tierGroupId: 'MISATO', threshold: 2, themeKeyword: 'Misato', themeTraits: THEME_DEFINITIONS.MISATO.traits },
  [BadgeCode.MISATO_III]: { code: BadgeCode.MISATO_III, category: BadgeCategory.THEME, tier: 'III', tierGroupId: 'MISATO', threshold: 3, themeKeyword: 'Misato', themeTraits: THEME_DEFINITIONS.MISATO.traits },

  [BadgeCode.YUSUKE_I]:   { code: BadgeCode.YUSUKE_I,   category: BadgeCategory.THEME, tier: 'I',   tierGroupId: 'YUSUKE', threshold: 2, themeKeyword: 'Yusuke', themeTraits: THEME_DEFINITIONS.YUSUKE.traits },
  [BadgeCode.YUSUKE_III]: { code: BadgeCode.YUSUKE_III, category: BadgeCategory.THEME, tier: 'III', tierGroupId: 'YUSUKE', threshold: 3, themeKeyword: 'Yusuke', themeTraits: THEME_DEFINITIONS.YUSUKE.traits },

  [BadgeCode.NERV_FULL]: { code: BadgeCode.NERV_FULL, category: BadgeCategory.THEME, threshold: 2, themeKeyword: 'NERV', themeTraits: THEME_DEFINITIONS.NERV.traits },

  [BadgeCode.HEART_I]:   { code: BadgeCode.HEART_I,   category: BadgeCategory.THEME, tier: 'I',   tierGroupId: 'HEART', threshold: 2, themeKeyword: 'Heart', themeTraits: THEME_DEFINITIONS.HEART.traits },
  [BadgeCode.HEART_III]: { code: BadgeCode.HEART_III, category: BadgeCategory.THEME, tier: 'III', tierGroupId: 'HEART', threshold: 3, themeKeyword: 'Heart', themeTraits: THEME_DEFINITIONS.HEART.traits },

  [BadgeCode.MOON_I]:   { code: BadgeCode.MOON_I,   category: BadgeCategory.THEME, tier: 'I',   tierGroupId: 'MOON', threshold: 2, themeKeyword: 'Moon', themeTraits: THEME_DEFINITIONS.MOON.traits },
  [BadgeCode.MOON_III]: { code: BadgeCode.MOON_III, category: BadgeCategory.THEME, tier: 'III', tierGroupId: 'MOON', threshold: 3, themeKeyword: 'Moon', themeTraits: THEME_DEFINITIONS.MOON.traits },

  [BadgeCode.BAD_HABIT]:  { code: BadgeCode.BAD_HABIT,  category: BadgeCategory.META, trait: Trait.MOUTH },
  [BadgeCode.POCKET_PET]: { code: BadgeCode.POCKET_PET, category: BadgeCategory.META, trait: Trait.NECK },
  [BadgeCode.GENESIS]:    { code: BadgeCode.GENESIS,    category: BadgeCategory.META, threshold: 100 },

  [BadgeCode.COLLECTOR_RANK_INITIATE]:     { code: BadgeCode.COLLECTOR_RANK_INITIATE,     category: BadgeCategory.COLLECTOR_RANK, tier: 'I',   tierGroupId: 'COLLECTOR_RANK', pointsThreshold: 100,  threshold: 100 },
  [BadgeCode.COLLECTOR_RANK_CONNOISSEUR]:  { code: BadgeCode.COLLECTOR_RANK_CONNOISSEUR,  category: BadgeCategory.COLLECTOR_RANK, tier: 'II',  tierGroupId: 'COLLECTOR_RANK', pointsThreshold: 500,  threshold: 500 },
  [BadgeCode.COLLECTOR_RANK_CURATOR]:      { code: BadgeCode.COLLECTOR_RANK_CURATOR,      category: BadgeCategory.COLLECTOR_RANK, tier: 'III', tierGroupId: 'COLLECTOR_RANK', pointsThreshold: 1500, threshold: 1500 },
  [BadgeCode.COLLECTOR_RANK_HALL_OF_FAME]: { code: BadgeCode.COLLECTOR_RANK_HALL_OF_FAME, category: BadgeCategory.COLLECTOR_RANK, tier: 'IV',  tierGroupId: 'COLLECTOR_RANK', pointsThreshold: 4000, threshold: 4000 },
};

export const ALL_BADGE_CODES: BadgeCode[] = Object.keys(BADGES) as BadgeCode[];

// Section ordering on the /badges page. COLLECTOR_RANK is intentionally
// omitted — the timeline at the top of the page renders that progression;
// rendering the same tier set again in a section grid would be redundant.
export const BADGE_CATEGORIES_IN_ORDER: BadgeCategory[] = [
  BadgeCategory.HOLDER,
  BadgeCategory.RARITY,
  BadgeCategory.COLLECTOR,
  BadgeCategory.THEME,
  BadgeCategory.COMPLETIONIST,
  BadgeCategory.COUNCIL,
  BadgeCategory.META,
];

// Tier groups, derived once from the catalog so the page can render
// stacked tiles without re-walking BADGES every render.
export const TIER_GROUPS_IN_ORDER: BadgeCode[][] = (() => {
  const byGroup = new Map<string, BadgeCode[]>();
  const singletons: BadgeCode[] = [];
  for (const code of ALL_BADGE_CODES) {
    const def = BADGES[code];
    if (def.tierGroupId) {
      const arr = byGroup.get(def.tierGroupId) ?? [];
      arr.push(code);
      byGroup.set(def.tierGroupId, arr);
    } else {
      singletons.push(code);
    }
  }
  for (const arr of byGroup.values()) {
    arr.sort((a, b) => (BADGES[a].threshold ?? 0) - (BADGES[b].threshold ?? 0));
  }
  return [...byGroup.values(), ...singletons.map((c) => [c])];
})();

// i18n key helpers.
export const badgeNameKey = (code: BadgeCode) => `BADGE_${code}_NAME`;
export const badgeDescriptionKey = (code: BadgeCode) =>
  `BADGE_${code}_DESCRIPTION`;
export const badgeCategoryTitleKey = (category: BadgeCategory) =>
  `BADGE_CATEGORY_${category}_TITLE`;
// For tier groups, we use the first tier's "group name" key. The assertion
// is safe: every group constructed by TIER_GROUPS_IN_ORDER contains at least
// one BadgeCode (an empty group never makes it into the array).
function firstCode(groupCodes: BadgeCode[]): BadgeCode {
  const c = groupCodes[0];
  if (!c) throw new Error('tier group is empty');
  return c;
}
export const tierGroupNameKey = (groupCodes: BadgeCode[]) => {
  const first = firstCode(groupCodes);
  return `BADGE_GROUP_${BADGES[first].tierGroupId ?? first}_NAME`;
};
export const tierGroupDescriptionKey = (groupCodes: BadgeCode[]) => {
  const first = firstCode(groupCodes);
  return `BADGE_GROUP_${BADGES[first].tierGroupId ?? first}_DESCRIPTION`;
};
